'use client';

import Image from 'next/image';
import { useState } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@/components/ui/carousel';
import type { MarketingLandingCopy } from '@/i18n/marketing-landing-copy';
import type { MarketingShowcaseMedia } from '@/components/marketing-landing/content';
import { MarketingSquare } from '@/components/marketing-landing/section-label';

export function RoleFeatureMedia({
  media,
  title,
  alt,
  controls,
  direction,
}: {
  media: MarketingShowcaseMedia | null;
  title: string;
  alt: string;
  controls: MarketingLandingCopy['showcaseControls'];
  direction: 'ltr' | 'rtl';
}) {
  const [api, setApi] = useState<CarouselApi>();
  const asset = media?.assets[0];
  const imageClassName = 'relative size-full';
  const imageSizes = '(min-width: 768px) 33vw, 100vw';

  return (
    <div className="bg-marketing-accent-subtle relative z-10 aspect-5/4 min-h-full min-w-0 overflow-hidden rounded-xl md:aspect-square md:rounded-2xl lg:rounded-3xl">
      {asset ? (
        media.mode === 'video' ? (
          <video
            src={asset.url}
            aria-label={alt}
            className="absolute inset-0 size-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            controls
            preload="metadata"
          />
        ) : media.mode === 'carousel' ? (
          <Carousel
            setApi={setApi}
            opts={{ loop: true, direction }}
            aria-label={alt}
            className="absolute inset-0 [&_[data-slot=carousel-content]]:h-full"
          >
            <CarouselContent className="ml-0 h-full">
              {media.assets.map((image, index) => (
                <CarouselItem key={`${image.url}-${index}`} className="pl-0">
                  <div className={imageClassName}>
                    <Image
                      src={image.url}
                      alt={alt}
                      fill
                      unoptimized
                      sizes={imageSizes}
                      className="object-cover"
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            {media.assets.length > 1 ? (
              <div className="absolute inset-x-3 bottom-3 flex justify-between">
                <button
                  type="button"
                  aria-label={controls.previous}
                  onClick={() => api?.scrollPrev()}
                  className="bg-marketing-cta text-marketing-cta-fg focus-visible:outline-marketing-accent-strong flex size-11 items-center justify-center rounded outline-none focus-visible:outline-2 focus-visible:outline-offset-2"
                >
                  <ArrowLeft
                    className="size-4 rtl:rotate-180"
                    aria-hidden="true"
                  />
                </button>
                <button
                  type="button"
                  aria-label={controls.next}
                  onClick={() => api?.scrollNext()}
                  className="bg-marketing-cta text-marketing-cta-fg focus-visible:outline-marketing-accent-strong flex size-11 items-center justify-center rounded outline-none focus-visible:outline-2 focus-visible:outline-offset-2"
                >
                  <ArrowRight
                    className="size-4 rtl:rotate-180"
                    aria-hidden="true"
                  />
                </button>
              </div>
            ) : null}
          </Carousel>
        ) : (
          <div className="absolute inset-0">
            <Image
              src={asset.url}
              alt={alt}
              fill
              unoptimized
              sizes={imageSizes}
              className="object-cover"
            />
          </div>
        )
      ) : (
        <div
          role="img"
          aria-label={alt}
          className="absolute inset-0 flex items-center justify-center px-8 pt-20 pb-8 text-center text-sm"
        >
          {alt}
        </div>
      )}
      {asset ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-black/25"
        />
      ) : null}
      <h3
        className="pointer-events-none absolute inset-x-4 top-4 flex items-start gap-3 text-xl leading-tight font-medium md:inset-x-5 md:top-5 md:text-[clamp(1rem,1.75vw,2.25rem)]"
        style={asset ? { color: 'var(--marketing-panel-fg)' } : undefined}
      >
        <MarketingSquare className="mt-1 bg-current md:mt-1.5 md:size-4" />
        {title}
      </h3>
    </div>
  );
}
